import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

import {
  DragDropContext,
  Droppable,
  Draggable,
} from "@hello-pangea/dnd";

import { GripVertical, RotateCw, ShieldCheck } from "lucide-react";

/* RANDOM NUMBER (0–99) */
const getSecureRandom = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] % 100;
};

const isAscendingOrder = (values) => {
  for (let i = 1; i < values.length; i++) {
    if (values[i] < values[i - 1]) return false;
  }
  return true;
};

/* Three numbers that start out of order. Each tile gets a stable id so
   drag-and-drop doesn't remount tiles when they move. */
const makePuzzle = () => {
  let values;
  do {
    values = Array.from({ length: 3 }, getSecureRandom);
  } while (isAscendingOrder(values));

  const seed = Date.now();
  return values.map((value, i) => ({ id: `n-${seed}-${i}`, value }));
};

const Captcha = forwardRef(({ onSolvedChange }, ref) => {

  const [tiles, setTiles] = useState(makePuzzle);
  const [isAscending, setIsAscending] = useState(false);
  const [spin, setSpin] = useState(0);

  const generateCaptcha = () => {
    setTiles(makePuzzle());
    setIsAscending(false);
    setSpin((s) => s + 1);
  };

  useEffect(() => {
    if (onSolvedChange) {
      onSolvedChange(isAscending);
    }
  }, [isAscending, onSolvedChange]);

  /* RESET (called by the contact form after a successful send) */
  useImperativeHandle(ref, () => ({
    resetCaptcha: generateCaptcha,
  }));

  /* DRAG END */
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = [...tiles];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setTiles(items);
    setIsAscending(isAscendingOrder(items.map((t) => t.value)));
  };

  return (
    <div className={`captcha-main ${isAscending ? "captcha-main--done" : ""}`}>

      <span className="captcha-status" aria-hidden="true">
        <ShieldCheck size={18} strokeWidth={2} />
      </span>

      <div className="captcha-copy" aria-live="polite">
        <strong>{isAscending ? "Verified" : "Quick check"}</strong>
        <span>
          {isAscending
            ? "You can send your message"
            : "Drag to sort, smallest first"}
        </span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="captcha-numbers" direction="horizontal">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="captcha-box-wrapper"
              aria-label="Numbers to sort"
            >
              {tiles.map((tile, index) => (
                <Draggable
                  key={tile.id}
                  draggableId={tile.id}
                  index={index}
                  isDragDisabled={isAscending}
                >
                  {(dragProvided, snapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      className={`captcha-box ${snapshot.isDragging ? "captcha-box--dragging" : ""}`}
                      aria-label={`Number ${tile.value}`}
                    >
                      <GripVertical className="captcha-box__grip" size={14} aria-hidden="true" />
                      <span>{tile.value}</span>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <button
        type="button"
        onClick={generateCaptcha}
        className="captcha-refresh"
        aria-label="Get new numbers"
        title="Get new numbers"
      >
        <RotateCw
          key={spin}
          size={16}
          className={spin ? "captcha-refresh__icon--spin" : undefined}
          aria-hidden="true"
        />
      </button>

    </div>
  );
});

/* DISPLAY NAME */
Captcha.displayName = "Captcha";

export default Captcha;
